import { readHeader, TRACE_ID_HEADER } from '@srm/core/errors';
import type { ApiPlugin } from '@srm/core/http';
import {
    describeRequestError,
    recordSupportError,
    reportSupportIssue,
    supportIssueOfRequest,
} from '@utils/supportErrorLog';
import { recordSupportRequest, supportRequestStart, type ISupportRequestStart } from '@utils/supportNetworkLog';

declare module 'axios' {
    interface AxiosRequestConfig {
        /** Фоновый запрос (опрос): его сбой не пишется в журнал ошибок для поддержки и в журнал запросов */
        skipSupportErrorLog?: boolean;
        /** Начало запроса для журнала запросов поддержки; ставит плагин `supportJournalPlugin` */
        supportRequestStart?: ISupportRequestStart;
        /**
         * Адрес для журналов поддержки вместо `url`, когда в пути — то, что вводил пользователь (поисковая фраза
         * сегментом): в журнал запросов, отметку и пакет диагностики уходит этот шаблон
         */
        supportLogUrl?: string;
    }
}

/**
 * Журналы для поддержки (сеансы поддержки): каждая попытка запроса — в журнал запросов (метод, путь, статус, время,
 * код обращения, размер); отказ — в журнал ошибок и, во время показа экрана, отметкой у специалистов (5xx и сеть —
 * всегда, 4xx — кроме 401 и 404 на чтение). Фоновые запросы (`skipSupportErrorLog`) — мимо обоих.
 */
export const supportJournalPlugin = (): ApiPlugin => ({
    name: 'support-journal',
    request: ({ config }) => {
        if (config && !config.skipSupportErrorLog) config.supportRequestStart = supportRequestStart();
    },
    settled: ({ config, response, outcome }) =>
        recordSupportRequest({
            method: config?.method,
            url: config?.supportLogUrl ?? config?.url,
            start: config?.supportRequestStart,
            status: response?.status,
            traceId: readHeader(response?.headers, TRACE_ID_HEADER),
            length: readHeader(response?.headers, 'content-length'),
            aborted: outcome === 'canceled',
        }),
    failure: ({ config, status, traceId }) => {
        if (config?.skipSupportErrorLog) return;
        const url = config?.supportLogUrl ?? config?.url;
        const text = describeRequestError(config?.method, url, status);
        recordSupportError(text, traceId);
        const issue = supportIssueOfRequest(config?.method, url, status, traceId, text);
        if (issue) reportSupportIssue(issue);
    },
});
